import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, computed, DestroyRef, effect, HostListener, inject, PLATFORM_ID, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { MaxUnitsNoticeComponent } from '@components/max-units-notice/max-units-notice.component';

import { CartService } from '@services/cart/cart-services';
import { CartValidationService } from '@services/cart/cart-validation.service';
import { CartItem } from '@services/cart/interfaces/cart.interfaces';
import { MAX_UNITS_PER_CART_ITEM } from '@shared/constants/constants';
import { ClpCurrencyPipe } from '@shared/pipes/clp-currency.pipe';

import { QuantitySelectorComponent } from '../catalog/product-detail/quantity-selector/quantity-selector.component';

const KEYCHAIN_VARIANT_LABELS: Record<string, string> = {
  mono: 'Monocolor',
  bi: 'Bicolor',
  tri: 'Tricolor',
  combination: 'Combinación',
};

@Component({
  selector: 'app-shopping-cart-drawer',
  standalone: true,
  imports: [CommonModule, QuantitySelectorComponent, ClpCurrencyPipe, MaxUnitsNoticeComponent],
  templateUrl: './shopping-cart-drawer.component.html',
  styleUrls: ['./shopping-cart-drawer.component.css'],
})
export class ShoppingCartDrawerComponent {
  public cartService = inject(CartService);

  /** Tope de unidades por línea; sobre esto se deriva a encargo personalizado. */
  public readonly maxUnitsPerItem = MAX_UNITS_PER_CART_ITEM;

  public readonly itemNamesWithoutDelivery = computed(() => this.cartValidation.itemNamesWithoutDelivery());

  private readonly router = inject(Router);
  private readonly cartValidation = inject(CartValidationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    effect(() => {
      if (!this.cartService.isCartDrawerOpen()) {
        return;
      }

      untracked(() => {
        this.cartValidation
          .revalidateAndAnnounce()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      });
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  public handleEscapeKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;

    if (keyboardEvent.key === 'Escape' && this.cartService.isCartDrawerOpen()) {
      this.cartService.closeCart();
    }
  }

  public isAtMaxUnits(item: CartItem): boolean {
    return item.quantity >= this.maxUnitsPerItem;
  }

  public goToCheckout(): void {
    this.cartService.closeCart();
    this.router.navigateByUrl('/pago');
  }

  public keychainVariantLabel(variant: string): string {
    return KEYCHAIN_VARIANT_LABELS[variant] ?? variant;
  }
}
